import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { useGetRecoveryConfigQuery } from 'api/recoveryAi'
import type { RecoveryConfig } from 'api/recoveryAi.types'
import Spinner from 'components/Spinner'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormControl, InputGroup } from 'react-bootstrap'
import ConfigCard from 'scenes/RecoveryAI/components/ConfigCard'
import { successNotificationOverrides } from 'scenes/RecoveryAI/components/successNotificationOverrides'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'
import {
  MAX_DAILY_BUDGET_DOLLARS,
  RECOVERY_LIMITLESS_BUDGET_CENTS,
} from '../constants'
import { useSaveAndTest } from './useSaveAndTest'
import { useUnsavedChangesPrompt } from './useUnsavedChangesPrompt'

const t = buildTranslate('recovery_ai.settings')
// Label / description / helper reuse the approved wizard copy (single proofread
// source) rather than duplicating it under settings.* (AI-576).
const tLimits = buildTranslate('recovery_ai.wizard.limits')

// $100k/day → cents; the persisted value is clamped to this before the PUT.
const MAX_DAILY_BUDGET_CENTS = MAX_DAILY_BUDGET_DOLLARS * 100

// AI-576 (F6): stable ids so the number field's aria-describedby can point at the
// currency addon, the helper constraint copy, and (when shown) the $0 notice. The
// pane is flag-gated + unmountInactive (a single instance on the page), so static
// ids are collision-free.
const CURRENCY_ID = 'daily-budget-currency'
const HELPER_TEXT_ID = 'daily-budget-helper'
const ZERO_NOTICE_ID = 'daily-budget-zero-notice'

// AI-576: post-activation Settings pane for the global daily budget
// (default_daily_budget_cents). Mirrors EscalationRulesPane (AI-33) — same
// read → staged local state → explicit Save skeleton via the shared
// useSaveAndTest hook — plus two budget-specific sub-states:
//   • the $0 notice (a real, merchant-selectable "issue nothing, escalate every
//     costed case" value), shown only when the current value is exactly 0; and
//   • the limitless-sentinel read-only state (CS/DB-set 2_147_483_647), which is
//     rendered read-only so the sentinel can never round-trip through the input.
// Flag-gated + unmountInactive at the tab (Settings/index.tsx), so the whole
// pane + its mutation state tear down on tab exit (no manual reset needed).
const DailyBudgetPane = () => {
  const [css] = useStyletron()
  // AI-64 parity: admin-only writes. View-only operators see the value
  // read-only (input disabled, Save footer withheld).
  const ability = useAbility()
  const canManage = ability.can('manage', 'MerchantSettings')
  const {
    data: config,
    isLoading,
    isError: isFetchError,
  } = useGetRecoveryConfigQuery()

  // Staged local dollar value as a STRING (matches useWizardHydration's
  // string-conversion + the DailyCapCard controlled input); parsed to cents only
  // at persist time.
  const [dollars, setDollars] = useState('')

  const storedCents = config?.default_daily_budget_cents
  // The stored sentinel is the only way a limitless value reaches the FE
  // (CS/DB-set). Render it read-only — never seed it into the editable field.
  const isLimitless = storedCents === RECOVERY_LIMITLESS_BUDGET_CENTS
  // AI-576 (F1): a CS/DB-set cap ABOVE the FE-editable max (raw SQL / a data
  // migration bypasses the model's disjoint validation — the same channel as the
  // sentinel). Render it read-only like the sentinel so a Save can never silently
  // clamp a >$100k cap down to $100k: the display/persist divergence the onChange
  // cap guards on WRITE, here on the hydrate/READ path.
  const isAboveEditableMax =
    storedCents != null && !isLimitless && storedCents > MAX_DAILY_BUDGET_CENTS
  // Any stored value the field can't faithfully edit is shown read-only.
  const isReadOnlyBudget = isLimitless || isAboveEditableMax

  const parsedDollars = Number(dollars)
  // A blank/non-finite/negative field is invalid — Save stays disabled so a
  // stray empty value can never persist as $0.
  const isValid =
    dollars.trim() !== '' &&
    Number.isFinite(parsedDollars) &&
    parsedDollars >= 0

  // dollars → cents, clamped to [0, MAX]. The input's `max` doesn't hard-block a
  // pasted/typed larger value, so clamp on send: the backend's rejected
  // (MAX_METERED, sentinel) gap is then unreachable from the FE by construction.
  const buildPayload = useCallback(
    (): Partial<RecoveryConfig> => ({
      default_daily_budget_cents: Math.min(
        Math.max(0, Math.round(Number(dollars) * 100)),
        MAX_DAILY_BUDGET_CENTS
      ),
    }),
    [dollars]
  )

  // Dirty only when a valid, differing value is entered. Never dirty in a
  // read-only state (sentinel / above-max — nothing editable to persist). With
  // out-of-range values diverted to read-only, every value reaching the compare
  // is already in [0, MAX], so buildPayload's clamp is a no-op here (like-for-like).
  const isDirty = useMemo(() => {
    if (!config || isReadOnlyBudget || !isValid) return false
    return buildPayload().default_daily_budget_cents !== storedCents
  }, [config, isReadOnlyBudget, isValid, buildPayload, storedCents])

  // AI-576 (F3): a 2xx that didn't persist the field (a pre-nexus#1077 backend
  // drops the unpermitted param and no-ops the update, echoing the OLD config)
  // must surface the save-error path, not a false "Saved" banner. Compare the
  // echoed cents to exactly what we submitted.
  const verifySaved = useCallback(
    (saved: RecoveryConfig, submitted: Partial<RecoveryConfig>) =>
      saved.default_daily_budget_cents === submitted.default_daily_budget_cents,
    []
  )

  const {
    handleSave,
    clearSuccess,
    saveSuccess,
    isSaving,
    isSaveError,
    canSave,
    guardBypassRef,
  } = useSaveAndTest(buildPayload, isDirty, verifySaved)

  // R3 parity: warn before leaving the route (in-app nav + refresh/close) while
  // dirty. Shared, pane-agnostic copy.
  const unsavedChangesPrompt = useUnsavedChangesPrompt(isDirty, guardBypassRef)

  // "Latest value" mirror of the in-flight flag so the [config] hydration effect
  // can read it without re-running when it flips (a false→true→false save cycle
  // would rehydrate from the stale cached config before the refetch lands).
  const isSavingRef = useRef(false)
  isSavingRef.current = isSaving

  // Hydrate the dollar field from the stored cents whenever a fresh config
  // arrives and no save is in flight. Skip the read-only values (sentinel +
  // above-max) and a null (defensive — the field is required, but never render
  // 'NaN'). Skipping the above-max value keeps the onChange cap from being
  // bypassed on the read path (which would show a >$100k value Save then clamps).
  useEffect(() => {
    if (!config) return
    if (isSavingRef.current) return
    const cents = config.default_daily_budget_cents
    if (
      cents == null ||
      cents === RECOVERY_LIMITLESS_BUDGET_CENTS ||
      cents > MAX_DAILY_BUDGET_CENTS
    )
      return
    const next = String(cents / 100)
    // Value-equality bailout: a query hook that yields a fresh `data` object each
    // render would otherwise loop (effect → setState → re-render → effect).
    setDollars(prev => (prev === next ? prev : next))
  }, [config])

  if (isLoading) {
    // The shared Spinner renders no role/aria text, so wrap it in a live status
    // region with an i18n accessible name (WCAG 4.1.2).
    return (
      <div role="status" aria-label={t('daily_budget_loading')}>
        <Spinner isLoading size="4x" className="text-center margin-top-huge" />
      </div>
    )
  }

  // A failed FETCH surfaces an error (not the misleading "locked" copy). Checked
  // before the onboarding gate so a load failure is never masked.
  if (isFetchError || !config) {
    return (
      <div className={`p-l ${css({ textAlign: 'center' })}`} role="alert">
        <Text.BodyText3 color="cayenne50">
          {t('daily_budget_fetch_error')}
        </Text.BodyText3>
      </div>
    )
  }

  if (!config.onboarding_completed) {
    return (
      <div className={`p-l ${css({ textAlign: 'center' })}`} role="status">
        <Text.BodyText3 color="grey70">
          {t('daily_budget_locked')}
        </Text.BodyText3>
      </div>
    )
  }

  if (isReadOnlyBudget) {
    return (
      <div className="p-l">
        <ConfigCard
          title={tLimits('daily_cap')}
          subtitle={tLimits('daily_cap_desc')}
        >
          <div role="status">
            <Text.BodyText3 color="grey70">
              {isLimitless
                ? t('daily_budget_unlimited')
                : t('daily_budget_above_max', {
                    max: MAX_DAILY_BUDGET_DOLLARS.toLocaleString('en-US'),
                  })}
            </Text.BodyText3>
          </div>
        </ConfigCard>
      </div>
    )
  }

  // The $0 notice is contextual to the current value, not just the stored one —
  // show it whenever the field reads exactly $0 (on load or after a live edit).
  const showZeroNotice = isValid && parsedDollars === 0

  // AI-576 (F6): describe the field with the currency + helper constraint (and the
  // $0 notice while it's visible), so a screen reader hears more than the bare
  // "Daily budget per location" label. Gate the $0-notice id on showZeroNotice so
  // the description tracks exactly what's rendered.
  const describedBy = [
    CURRENCY_ID,
    HELPER_TEXT_ID,
    showZeroNotice ? ZERO_NOTICE_ID : null,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="p-l">
      {/* R3: guard whole-route navigation while dirty. */}
      {unsavedChangesPrompt}
      {isSaveError && (
        <Notification kind="negative" size="full">
          {t('daily_budget_save_error')}
        </Notification>
      )}
      {saveSuccess && !isSaveError && (
        <Notification
          kind="positive"
          size="full"
          overrides={successNotificationOverrides}
        >
          {t('daily_budget_save_success')}
        </Notification>
      )}
      {showZeroNotice && (
        // Informational guidance, not an error — announce politely (role="status")
        // via the shared override so it doesn't assertively interrupt as the
        // merchant types 0. Same treatment the success banner above uses. The id
        // lets the input's aria-describedby reference it (F6).
        <div id={ZERO_NOTICE_ID}>
          <Notification
            kind="warning"
            size="full"
            overrides={successNotificationOverrides}
          >
            {t('daily_budget_zero_notice')}
          </Notification>
        </div>
      )}
      <ConfigCard
        title={tLimits('daily_cap')}
        subtitle={tLimits('daily_cap_desc')}
      >
        <InputGroup>
          <InputGroup.Addon id={CURRENCY_ID}>$</InputGroup.Addon>
          <FormControl
            type="number"
            min={0}
            max={MAX_DAILY_BUDGET_DOLLARS}
            step={1}
            value={dollars}
            disabled={!canManage}
            onChange={(e: React.FormEvent<FormControl>) => {
              const raw = (e.target as HTMLInputElement).value
              const entered = Number(raw)
              // Whole-dollar field (F4): a daily reward budget is never sub-dollar,
              // so normalize a finite numeric entry to a rounded whole dollar,
              // capped at the max. Normalizing here also keeps a sub-cent value
              // ($0.004) from reaching state, so the $0 notice and the write can't
              // disagree (F2). Empty/partial/negative input passes through so the
              // isValid gating still disables Save on a blank or negative field;
              // buildPayload's send-clamp stays as defense-in-depth.
              const isNumericEntry =
                raw.trim() !== '' && Number.isFinite(entered)
              setDollars(
                isNumericEntry
                  ? String(
                      Math.min(Math.round(entered), MAX_DAILY_BUDGET_DOLLARS)
                    )
                  : raw
              )
              // Drop any stale "Saved" banner as soon as the value changes.
              clearSuccess()
            }}
            aria-label={tLimits('daily_cap')}
            aria-describedby={describedBy}
          />
        </InputGroup>
        {/* F6: wrap the helper in an id'd element (uikit Text drops `id`) so the
            input's aria-describedby can reference the constraint copy. */}
        <div id={HELPER_TEXT_ID} className="mt-xs">
          <Text.BodyText5 color="grey70">
            {tLimits('daily_cap_helper')}
          </Text.BodyText5>
        </div>
      </ConfigCard>
      {/* AI-64 parity: the Save footer is admin-only. */}
      {canManage && (
        <div className="mt-l">
          <Button
            kind="primary"
            onClick={handleSave}
            disabled={!canSave}
            isLoading={isSaving}
          >
            {t('daily_budget_save')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default DailyBudgetPane
